import type { GameState, SeasonRewardClaim } from "../domain/types";
import type { Translator } from "../i18n/locales";
import {
  formatSeasonCountdown,
  formatUsdtCents,
  formatUsdtMicros,
  getSeasonCountdownTarget,
} from "./formatters";

export type SeasonHeaderMode = "active" | "claim" | "upcoming";

export type SeasonHeaderPresentation = {
  mode: SeasonHeaderMode;
  label: string;
  value: string;
  countdown: string;
  ariaLabel: string;
};

export function pendingSeasonClaim(state: GameState): SeasonRewardClaim | null {
  return state.seasonClaims.find((claim) => claim.status === "claimable") ?? null;
}

export function seasonHeaderPresentation(
  state: GameState,
  serverNow: string,
  t: Translator,
): SeasonHeaderPresentation {
  const locale = state.settings.locale;
  const pendingClaim = pendingSeasonClaim(state);
  if (pendingClaim !== null) {
    const value = formatUsdtMicros(pendingClaim.referenceUsdtMicros, locale);
    const countdown = formatSeasonCountdown(pendingClaim.claimClosesAt, serverNow, locale);
    return {
      mode: "claim",
      label: t("progress.rewardReadyTitle"),
      value,
      countdown,
      ariaLabel: `${t("progress.rewardReadyTitle")}: ${value}. ${t("header.claimCloses")}: ${countdown}`,
    };
  }

  if (state.season.status === "claimable") {
    const value = formatUsdtCents(state.season.projection.estimatedRewardUsdCents, locale, true);
    const countdown = formatSeasonCountdown(getSeasonCountdownTarget(state.season), serverNow, locale);
    return {
      mode: "claim",
      label: t("progress.rewardReadyTitle"),
      value,
      countdown,
      ariaLabel: `${t("progress.rewardReadyTitle")}: ${value}. ${t("header.claimCloses")}: ${countdown}`,
    };
  }

  const countdown = formatSeasonCountdown(getSeasonCountdownTarget(state.season), serverNow, locale);
  if (state.season.status === "scheduled") {
    return {
      mode: "upcoming",
      label: t("header.seasonStarts"),
      value: countdown,
      countdown: "",
      ariaLabel: `${t("header.seasonStarts")}: ${countdown}`,
    };
  }

  if (state.season.status === "frozen" || state.season.status === "settling") {
    return {
      mode: "upcoming",
      label: t("progress.seasonFrozenTitle"),
      value: t("progress.seasonSettlingTitle"),
      countdown: "",
      ariaLabel: `${t("progress.seasonFrozenTitle")}. ${t("progress.seasonFrozenBody")}`,
    };
  }

  const value = formatUsdtCents(state.season.prizeFundUsdCents, locale);
  return {
    mode: "active",
    label: t("header.seasonFund"),
    value,
    countdown,
    ariaLabel: `${t("header.seasonFund")}: ${value}. ${t("header.season")}: ${countdown}`,
  };
}
